// NPM Imports
import React, { Component } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { connect } from "react-redux";
import { Button } from "../../../revvComponents";

// Internal Imports
import { GRID, PERMISSIONS, EVENTS } from "../../../constants";
import { AmplitudeUtils } from "../../../utils";

// Components Imports
import { ConfirmationBox } from "../../../commonComponents";
import withRbac from "../../../app/withRbac";

// Actions Imports
import { DealTableActions } from "../../../actions";

// Destructuring
const { DEALTABLE_GROUP_LIST } = GRID;
const { onGroupApply, onGroupDelete } = DealTableActions;

class DealTableBar extends Component {
  static selectedGroup = "";

  // Added this for amplitude
  static selectedGroupName = "";

  myRef = React.createRef();

  state = {
    filterSelected: false,
    popupVisible: false,
    selectedFilter: "",
    showConfirmation: false,
  };

  componentDidMount() {
    /**
     *  If groups are applied then pass it to state so that whenever
     *  user loads the page the applied group will be selected and displayed
     *  in dealtable top bar
     */
    const {
      discount: { groups = [] },
    } = this.props;

    if (groups && groups.length > 0) {
      const column = groups[0].group_by.toLowerCase();
      this.setState({ filterSelected: true, selectedFilter: column });
    }
  }

  componentDidUpdate = prevProps => {
    if (
      prevProps.rowData !== this.props.rowData &&
      this.props.rowData.length === 0
    ) {
      this.setState({ filterSelected: false, selectedFilter: "" });
    }
  };

  _handleGroupListClick = () => {
    // attach/remove event handler
    if (!this.state.popupVisible) {
      document.addEventListener("click", this._handleOutsideClick, false);
    } else {
      document.removeEventListener("click", this._handleOutsideClick, false);
    }

    this.setState(prevState => ({
      popupVisible: !prevState.popupVisible,
    }));
  };

  _handleOutsideClick = () => {
    this._handleGroupListClick();
  };

  /**
   * _togglePopUp func handles the modal popup visibility
   *
   * @param {string} type - Refers to the state name
   * @param {boolean} flag - Decides the popup should be visible/hidden
   */
  _togglePopUp = (type, flag) => {
    this.setState({ [type]: flag });
  };

  /**
   * _applyGrouping func handles the group adding logic
   * @param {string} column - Holds the grid column name
   */

  _applyGrouping = (column, name) => {
    if (!this.props.isLocked) {
      const { dealTableId, discount, fieldMapIndex, rowData } = this.props;

      // Execute the below func only when grid api is available
      if (rowData.length > 0) {
        if (this.state.selectedFilter) {
          DealTableBar.selectedGroup = column;
          DealTableBar.selectedGroupName = name;
          this._togglePopUp("showConfirmation", true);
          return false;
        }
        // Generate group JSON when applied
        this.props
          .onGroupApply(rowData, discount, column, fieldMapIndex, dealTableId)
          .then(() => {
            /**
             * Once the JSON is created & saved in redux, call a ag-grid method to set
             * the grouping logic.
             */
            this.props.groupKeyReference(this.props.fieldMapIndex[column]);
            // Implementing amplitude for group applied
            AmplitudeUtils.amplitudeLogEvent(
              EVENTS.EVENT_NAMES.DEAL_TABLE_GROUPING_APPLIED,
              { groupType: name },
            );
            // Update the local state for the UI to behave based on current selection
            this.setState({
              filterSelected: true,
              selectedFilter: column,
            });
          });
      }
      return true;
    }
    return false;
  };

  _removeGrouping = () => {
    const { dealTableId, discount, fieldMapIndex, rowData } = this.props;
    const column = DealTableBar.selectedGroup;
    const name = DealTableBar.selectedGroupName;
    // When removing a group make an API to BackEnd to do the same
    this.props
      .onGroupDelete(rowData, discount, dealTableId, fieldMapIndex)
      .then(updatedDiscount => {
        /**
         *  When user selects different groups, exising has to be removed
         *  and new grouping has to be applied, this logic has to happen in sequence.
         */
        this.props.groupKeyReference();
        if (column) {
          // Generate group JSON when applied
          this.props
            .onGroupApply(
              rowData,
              updatedDiscount,
              column,
              fieldMapIndex,
              dealTableId,
            )
            .then(() => {
              /**
               * Once the JSON is created & saved in redux, call a ag-grid method to set
               * the grouping logic.
               */
              this.props.groupKeyReference(this.props.fieldMapIndex[column]);
              // Implemented amplitude for grouping modified
              AmplitudeUtils.amplitudeLogEvent(
                EVENTS.EVENT_NAMES.DEAL_TABLE_GROUPING_MODIFIED,
                { modifiedTo: name },
              );
            });
        } else {
          // Implemented amplitude for groupi ng modified to remove
          AmplitudeUtils.amplitudeLogEvent(
            EVENTS.EVENT_NAMES.DEAL_TABLE_GROUPING_MODIFIED,
            { modifiedTo: "Removed" },
          );
        }
        this.setState({
          filterSelected: !!column,
          selectedFilter: column || "",
          showConfirmation: false,
        });
        // Reset the static selectedGroup key to restart the cycle
        DealTableBar.selectedGroup = "";
      });
  };

  render() {
    const { isLocked, togglePopUp } = this.props;
    const { filterSelected, selectedFilter, popupVisible } = this.state;

    // Show manage column btn based on user roles
    const ManageColumnsBtn = withRbac(() => (
      <Button
        data-test="dealtablebar-manage-column-button"
        className="btn-default dealtable-settings"
        type="six"
        onClick={() => togglePopUp("manageColumnPopUp", true)}>
        <i>
          <FontAwesomeIcon icon={["far", "eye"]} />
        </i>
        Manage columns
      </Button>
    ));

    return (
      <div className="dealtablebar-wrapper">
        <div className="dealtable-controls">
          {/* Section to show dealtable group button */}
          <section className="dealtable-groups">
            <div className="controlbar-group-wrapper">
              <div
                data-test="dealtablebar-group-icon"
                className={`controlbar-action-title btn-default ${
                  this.state.filterSelected ? "is-selected" : ""
                } ${popupVisible ? "popup-visible" : ""}`}
                onClick={this._handleGroupListClick}
                onKeyPress={this._handleGroupListClick}
                role="button"
                tabIndex={0}>
                <span className="icon">
                  <FontAwesomeIcon icon={["far", "object-group"]} />
                </span>
                {filterSelected ? "Grouped" : "Group"}
                <i>
                  <FontAwesomeIcon icon={["far", "angle-down"]} />
                </i>
              </div>
              {popupVisible && (
                <div
                  data-test="dealtablebar-group-list"
                  className="controlbar-group-list"
                  ref={this.myRef}>
                  <ul className="controlbar-group">
                    {DEALTABLE_GROUP_LIST.map(item => (
                      <li
                        key={item.alias}
                        className={`controlbar-group-item ${
                          filterSelected && selectedFilter === item.alias
                            ? "is-selected"
                            : ""
                        }`}>
                        <span
                          data-test={`dealtablebar-group-item-${item.alias}-button`}
                          className="controlbar-group-itemtitle"
                          // passed {item.name} for apmlitude
                          onClick={() =>
                            this._applyGrouping(item.alias, item.name)
                          }
                          onKeyPress={() =>
                            this._applyGrouping(item.alias, item.name)
                          }
                          role="button"
                          tabIndex="0">
                          <span className="controlbar-group-ico">
                            <FontAwesomeIcon icon={["fas", `${item.icon}`]} />
                          </span>
                          {item.name}
                        </span>
                        {!isLocked &&
                          filterSelected &&
                          selectedFilter === item.alias && (
                            // When a grouping is applied it will automatically redirected
                            // to _removeGrouping func hence _applyGrouping is used below
                            <span
                              className="remove"
                              onClick={() => this._applyGrouping("")}
                              onKeyPress={() => this._applyGrouping("")}
                              role="button"
                              tabIndex="0">
                              <FontAwesomeIcon icon={["far", "times-circle"]} />
                            </span>
                          )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>

          {/* Section to show confirmation msg when groupBy option is changed or removed */}
          <section>
            <ConfirmationBox
              show={this.state.showConfirmation}
              closePopup={() => this._togglePopUp("showConfirmation", false)}
              header="Are you sure you want to modify grouping ?"
              subHeader="Once modified, applied group discount data will be lost"
              checkboxLabel="Yes, I want to modify"
              confirmAction={this._removeGrouping}
              okBtnText="Modify grouping"
            />
          </section>

          {/* Section to show manage columng popup button */}
          <section className="dealtable-managecolumn">
            <ManageColumnsBtn
              permission={PERMISSIONS.SETTINGS.MANAGE_COMPANY_SETTINGS}
            />
          </section>

          {/* Section to show numerical rounding popup button 
          <section className="dealtable-numerical-rounding">
            <Button
              className="btn-default dealtable-settings"
              type="button"
              disabled={rowData.length === 0 || isLocked}
              onClick={() =>
                this.props.togglePopUp("manageNetPriceRoundingPopUp", true)
              }>
              <i>
                <FontAwesomeIcon icon={["far", "box-usd"]} />
              </i>
              Net price rounding
            </Button>
          </section>
          */}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const {
    discount: discountObj,
    metaData: { fieldMapIndex },
    rowData: rowDataObj,
  } = state.DealTableStore;
  const { dealTableId } = state.QuotationStore;
  const {
    document: { isLocked },
  } = state.DocumentStore;

  // Pick out the required data from redux
  const rowData = rowDataObj[dealTableId] || [];
  const discount = discountObj[dealTableId] || {};

  return { dealTableId, discount, fieldMapIndex, isLocked, rowData };
};

const mapDispatchToProps = {
  onGroupApply,
  onGroupDelete,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealTableBar);
